import {useState} from 'react'
import {useNavigate} from 'react-router-dom'
import {useTranslation} from 'react-i18next'
import {Dialog, DialogContent, DialogHeader, DialogTitle} from '@/components/ui/dialog'
import {Input} from '@/components/ui/input'
import {Skeleton} from '@/components/ui/skeleton'
import {Tag} from '@/components/ui/tag'
import {PaginationBar} from '@/components/ui/pagination-bar'
import {useBusinesses, BUSINESSES_PAGE_SIZE} from '@/hooks/empresa/organizacion/use-businesses'
import {useDebouncedValue} from '@/hooks/common/use-debounced-value'
import {SYSTEM_NIT} from '@/lib/constants'
import {tope} from '@/lib/campos-texto'

export function BusinessSwitcherDialog({open, onOpenChange}: {open: boolean; onOpenChange: (open: boolean) => void}) {
  const {t} = useTranslation()
  const navigate = useNavigate()
  const [page, setPage] = useState(1)
  const [searchInput, setSearchInput] = useState('')
  const search = useDebouncedValue(searchInput, 300)

  const {data, isLoading} = useBusinesses({page, search, sortBy: 'name', sortDir: 'asc'})

  function handleOpenChange(next: boolean) {
    onOpenChange(next)
    if (!next) {
      setSearchInput('')
      setPage(1)
    }
  }

  function jumpTo(businessId: string) {
    handleOpenChange(false)
    navigate(`/businesses/${businessId}`)
  }

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t('switcher.title')}</DialogTitle>
        </DialogHeader>

        <Input
          maxLength={tope('BUSCA')}
          autoFocus
          value={searchInput}
          onChange={(e) => {
            setSearchInput(e.target.value)
            setPage(1)
          }}
          placeholder={t('switcher.searchPlaceholder')}
        />

        {isLoading && !data ? (
          <Skeleton className="h-40 w-full" />
        ) : data?.items.length ? (
          <div className="flex max-h-80 flex-col overflow-y-auto">
            {data.items.map((business) => (
              <button
                key={business.id}
                type="button"
                onClick={() => jumpTo(business.id)}
                className="flex items-center justify-between gap-3 border-b border-border px-1 py-2.5 text-left outline-none last:border-0 hover:bg-secondary/60 focus-visible:bg-secondary/60">
                <span className="min-w-0">
                  <span className="block truncate text-sm font-medium text-foreground">{business.name}</span>
                  <span className="font-mono text-xs text-muted-foreground">{business.nit}</span>
                </span>
                <span className="flex shrink-0 items-center gap-1.5">
                  {business.nit === SYSTEM_NIT && <Tag tone="ink">{t('businesses.systemTag')}</Tag>}
                  <Tag tone="neutral">{t(`roles.${business.role}`)}</Tag>
                </span>
              </button>
            ))}
          </div>
        ) : (
          <p className="py-6 text-center text-sm text-muted-foreground">{t('switcher.empty')}</p>
        )}

        {!!data && data.total > BUSINESSES_PAGE_SIZE && (
          <PaginationBar page={page} pageSize={BUSINESSES_PAGE_SIZE} total={data.total} onPageChange={setPage} />
        )}
      </DialogContent>
    </Dialog>
  )
}
